<template>
	<v-sheet rounded>
		<v-card color="#125B92">
			<v-card-title v-if="titleBar" class="align-center">
				<v-icon left large color="#E3ECF2">mdi-briefcase-check</v-icon>
				<h3 class="white--text">{{ title }}</h3>
				<v-spacer />
				<slot />
			</v-card-title>

			<v-divider v-if="titleBar" class="thick" />

			<v-data-table
				:headers="tableHeaders"
				:items="items"
				:loading="loading"
				:footer-props="{
					'items-per-page-options': [15, 30, 45, 60, 75]
				}"
				:page.sync="filters.page"
				:items-per-page.sync="filters.paginated"
				:server-items-length="totalProjects"
				:sort-by.sync="filters.sort"
				:sort-desc.sync="filters.descending"
				class="table-stripped"
				show-expand
				@item-expanded="getProjectProperties"
				@update:page="$vuetify.goTo(0)">
				<template #top="{ pagination, options, updateOptions }">
					<v-toolbar flat>
						<div class="flex-fill ml-3 datatable-filter-large">
							<v-text-field
								v-model.trim="filters.search"
								class="table-search"
								append-icon="mdi-magnify"
								placeholder="Search Project Data..."
								name="search"
								autocomplete="off"
								single-line
								clearable
								filled
								dense
								outlined
								full-width
								autofocus
								hide-details />
						</div>
						<div class="ml-3 datatable-filter-large">
							<v-text-field
								v-model.trim="filters.property"
								class="table-search"
								append-icon="mdi-magnify"
								placeholder="Search Asset Address..."
								name="property"
								autocomplete="off"
								single-line
								clearable
								filled
								dense
								outlined
								full-width
								autofocus
								hide-details />
						</div>
						<div v-if="! simplify" class="ml-3 datatable-filter-small">
							<v-select
								v-model="filters.client"
								:items="clients"
								placeholder="Filter by Client"
								item-text="name"
								item-value="id"
								single-line
								clearable
								filled
								dense
								outlined
								hide-details></v-select>
						</div>
						<div v-if="! simplify" class="ml-3 datatable-filter-small">
							<v-select
								v-model="filters.status"
								:items="statuses"
								placeholder="Filter by Status"
								item-text="name"
								item-value="id"
								single-line
								clearable
								filled
								dense
								outlined
								hide-details></v-select>
						</div>
						<div v-if="auth.user_type === 2" class="ml-3 mt-3">
							<v-checkbox
								v-model="filters.assignedToMe"
								label="Assigned to Me"
								:true-value="true"
								:false-value="null"
								class="text-no-wrap"
								filled
								dense
								outlined></v-checkbox>
						</div>
						<v-data-footer
							:pagination="pagination"
							:options="options"
							:items-per-page-options="[15, 30, 45, 60, 75]"
							items-per-page-text="$vuetify.dataTable.itemsPerPageText"
							@update:options="updateOptions" />
					</v-toolbar>
				</template>
				<template #item.id="{ item }">
					<Link :href="route('projects.show', item)">{{ item.id }}</Link>
				</template>

				<template #item.name="{ item }">
					<Link v-if="item" :href="route('projects.show', item)">
						<span v-if="item.name">{{ item.name }}</span>
						<em v-else class="grey--text text--darken-2">Unknown Name</em>
					</Link>
					<span v-else>--</span>
				</template>

				<template #item.status="props" :class="props.item.status.color">
					<td :class="props.item.status.color" class="status text-center">
						<v-edit-dialog
							:return-value.sync="props.item.status"
							large
							@save="updateStatus(props.item)">
							<span class="status text--darken-4" :class="props.item.status.color.split(' ')[0] + '--text'">{{ props.item.status.name }}</span>
							<template #input>
								<v-select
									v-model="props.item.status"
									:items="statuses"
									item-text="name"
									item-value="id"
									class="mt-3"
									return-object
									filled
									dense
									hide-details
									single-line></v-select>
							</template>
						</v-edit-dialog>
					</td>
				</template>

				<template #item.due_date="{ item }">
					<span v-if="item.due_date">
						{{ item.due_date | localDate }}
					</span>
					<span v-else class="grey--text">Unknown</span>
				</template>

				<template #item.latest_property="{ item }">
					<div v-if="!item.properties_count">--</div>
					<div v-else-if="item.properties_count > 1" class="d-flex align-center">
						<v-icon class="pr-2">mdi-format-list-bulleted</v-icon>
						<em>{{ item.properties_count }} Asset Addresses</em>
					</div>
					<div v-else-if="item.latest_property">
						<div>
							<span v-html="item.latest_property['full_address_stacked']"></span>
						</div>
					</div>
					<span v-else>--</span>
				</template>

				<template #item.relationship="{ item }">
					<v-chip v-if="!item.properties_count" small>{{ item.properties_count }}</v-chip>
					<v-chip v-else-if="item.properties_count > 1" small color="success">{{ item.properties_count }}</v-chip>
					<v-chip v-else small>{{ item.latest_property['asset_relationship'] }}</v-chip>
				</template>

				<template #item.orderer="{ item }">
					<span v-if="item.orderer">{{ item.orderer.name }}</span>
					<span v-else>--</span>
				</template>

				<template #item.client="{ item }">
					<template v-if="item.client">
						<span v-if="auth.user_type === 1">
							<Link v-if="item.client" :href="route('clients.show', item.client)">{{ item.client.name }}</Link>
						</span>
						<span v-else>
							{{ item.client.name }}
						</span>
					</template>
					<template v-else>
						--
					</template>
				</template>

				<template #expanded-item="{ headers, item }">
					<td :colspan="headers.length" class="pa-4 ma-0">
						<project-expand :project="item"></project-expand>
					</td>
				</template>
			</v-data-table>
		</v-card>
	</v-sheet>
</template>
<script>
import _ from 'lodash'
import api from '@/api'
import ProjectExpand from '@/Pages/Backend/Projects/Components/ProjectExpand'

export default {
	components: { ProjectExpand },
	props: {
		title: {
			type: String,
			default: 'Projects'
		},
		projects: {
			type: [Array, Object],
			default: () => {},
			required: true
		},
		simplify: {
			type: Boolean,
			default: () => false
		},
		titleBar: {
			type: Boolean,
			default: () => false
		}
	},
	data () {
		return {
			filters: {
				search: '',
				property: '',
				status: '',
				client: '',
				assignedToMe: null,
				paginated: 75,
				page: 1,
				descending: true,
				sort: 'id'
			},
			statuses: [],
			clients: [],
			loading: true,
			items: [],
			tableHeaders: [
				{
					text: '#',
					align: 'start',
					value: 'id'
				},
				{
					text: 'Name',
					align: 'start',
					value: 'name'
				},
				{
					text: 'Asset Address',
					sortable: false,
					align: 'start',
					value: 'latest_property'
				},
				{
					text: 'Client',
					sortable: false,
					align: 'start',
					value: 'client'
				},
				{
					text: 'Orderer',
					sortable: false,
					align: 'start',
					value: 'orderer'
				},
				{
					text: 'Due Date',
					align: 'start',
					value: 'due_date'
				},
				{
					text: 'Status',
					align: 'center',
					sortable: false,
					value: 'status'
				}
			],
			auth: this.$page.props.auth
		}
	},
	computed: {
		totalProjects () {
			if (this.projects && this.projects.hasOwnProperty('meta')) {
				return this.projects.meta.total
			}

			return 0
		}
	},
	watch: {
		'filters.search': {
			handler: _.debounce(function (newVal, oldVal) {
				if (newVal && newVal !== oldVal || !newVal && oldVal) {
					this.getProjects()
				}
			}, 350)
		},

		'filters.property': {
			handler: _.debounce(function (newVal, oldVal) {
				if (newVal && newVal !== oldVal || !newVal && oldVal) {
					this.getProjects()
				}
			}, 350)
		},

		projects () {
			if (this.projects.hasOwnProperty('data')) {
				this.items = this.projects.data
				this.loading = false
			} else {
				this.items = []
			}
		}
	},
	mounted () {
		this.getProjectStatuses()
		this.getClients({ active: 'true' })

		if (this.projects.hasOwnProperty('data')) {
			this.items = this.projects.data
			this.loading = false
		}

		if (this.$page.props.filters) {
			this.filters = this.$page.props.filters
		}

		this.$watch(vm => [vm.filters.paginated, vm.filters.assignedToMe, vm.filters.page, vm.filters.status, vm.filters.client, vm.filters.sort, vm.filters.descending], val => {
			this.getProjects()
		})

		if (this.simplify) {
			// Remove some default columns
			this.tableHeaders = _.remove(this.tableHeaders, header => !['client', 'latest_property'].includes(header.value))
		}
	},
	methods: {
		getProjectStatuses () {
			const method = 'GET'
			const data = {}

			const url = this.route('project-statuses.api.index')

			return api({
				method,
				url,
				data
			}).then(response => {
				this.statuses = response.data
			})
		},

		getProjects () {
			this.$inertia.reload({
				onStart: () => this.loading = true,
				onFinish: () => {
					this.loading = false

					if(! this.length(this.items) && this.filters.page > 1) {
						this.filters.page = 1
					}
				},
				only: ['projects'],
				data: this.filters
			})
		},

		getProjectProperties ({ item }) {
			const method = 'GET'
			const data = {}
			const url = this.route('projects.api.properties', { project_id: item.id })

			return api({
				method,
				url,
				data
			}).then(response => {
				this.$set(item, 'properties', response.data)
			})
		},

		updateStatus (project) {
			const data = { status_id: project.status.id }
			this.$inertia.put(this.route('projects.update.status', project), data, {
				only: ['projects', 'flash'],
				onFinish: () => this.statusMenu = false,
				preserveState: true,
				preserveScroll: true
			})
		}
	}
}
</script>
